/**
 * @file Board footer toggle renderer and task horizon inline preference control.
 */

import {
    appData,
    state,
    getEffectiveViewLayout,
} from '../../app/state/state.js';
import { updateUserDocument } from '../../domains/settings/settingsApi.js';
import { getBoardRegistryEntryByItemType } from './boardRegistry.js';
import { applyBrandingVariables } from '../ui/brandingTokens.js';
import {
    getCurrentUserTaskHorizonDays,
    getTaskHorizonMinimumDays,
    parseTaskHorizonDays,
} from "../utils/taskVisibility.js";

async function rerenderCurrentBoard() {
    const { renderCurrentBoard } = await import('../../app/view/viewController.js');
    renderCurrentBoard();
}

function renderBoardFooterSwitch({ id, checked, label }) {
    return `
        <label class="board-footer-switch shrink-0">
            <input type="checkbox" id="${id}" aria-label="${label}" ${checked ? 'checked' : ''}>
            <span class="board-footer-switch-track"></span>
        </label>
    `;
}

/**
 * Renders footer controls for parent board views.
 * @param {string} viewType Singular board type.
 * @returns {void}
 */
export function renderFooterToggle(viewType) {
    const registryEntry = getBoardRegistryEntryByItemType(viewType);
    const viewKey = registryEntry?.viewKey || `${viewType}s`;
    const settings = state.viewSettings?.[viewKey];
    const footerContainer = document.getElementById(`${viewType}-board-footer`);

    if (!footerContainer || !settings) {
        if (footerContainer) {footerContainer.innerHTML = '';}
        return;
    }
    applyBrandingVariables(footerContainer, appData);

    const isClosedChecked = state.showClosed || false;
    let togglesHtml = '';
    let taskHorizonControl = '';

    const supportsWipToggle = registryEntry?.capabilities?.supportsWip !== false;

    if (viewType !== 'task' && supportsWipToggle && getEffectiveViewLayout(viewKey) !== 'table') {
        const isWIPChecked = state.showWIP;
        togglesHtml += `
            <div class="flex items-center gap-3">
                <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Detail View</span>
                ${renderBoardFooterSwitch({
                    id: `detail-view-toggle-${viewType}`,
                    checked: isWIPChecked,
                    label: "Detail View",
                })}
            </div>
        `;
    }

    // This toggle now shows for all views (table and kanban)
    togglesHtml += `
        <div class="flex items-center gap-3">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Show Closed</span>
            ${renderBoardFooterSwitch({
                id: `show-closed-toggle-${viewType}`,
                checked: isClosedChecked,
                label: "Show Closed",
            })}
        </div>
    `;

    if (viewType === 'task') {
        const minimum = getTaskHorizonMinimumDays();
        const currentValue = state.currentUserProfile?.taskVisibilityHorizonDays;
        const effectiveValue = getCurrentUserTaskHorizonDays();
        const hasUserValue = !(currentValue === null || currentValue === undefined || currentValue === '');
        const defaultActivateValue = minimum ?? 30;

        taskHorizonControl = `
            <div class="mr-auto flex flex-wrap items-center gap-1.5">
                <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Show tasks due</span>
                ${hasUserValue
                    ? `<button
                        type="button"
                        id="task-horizon-inline-value"
                        data-default-value="${defaultActivateValue}"
                        class="rounded-sm px-1 text-sm font-medium text-gray-700 underline decoration-gray-300 underline-offset-4 transition-colors hover:text-[var(--dfc-brand-primary)] hover:decoration-[var(--dfc-brand-primary)] focus:outline-none focus:shadow-[inset_0_-2px_0_rgba(71,85,105,0.55)] dark:text-gray-200"
                    >within ${effectiveValue} days</button>`
                    : `<button
                        type="button"
                        id="task-horizon-inline-value"
                        data-default-value="${defaultActivateValue}"
                        class="rounded-sm px-1 text-sm font-medium text-gray-700 underline decoration-gray-300 underline-offset-4 transition-colors hover:text-[var(--dfc-brand-primary)] hover:decoration-[var(--dfc-brand-primary)] focus:outline-none focus:shadow-[inset_0_-2px_0_rgba(71,85,105,0.55)] dark:text-gray-200"
                    >anytime</button>`
                }
            </div>
        `;
    }

    footerContainer.innerHTML = `
        <div class="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3">
            ${taskHorizonControl}
            <div class="ml-auto flex flex-wrap items-center gap-x-6 gap-y-3">
                ${togglesHtml}
            </div>
        </div>
    `;

    const closedToggle = footerContainer.querySelector(`#show-closed-toggle-${viewType}`);
    const wipToggle = footerContainer.querySelector(`#detail-view-toggle-${viewType}`);

    if (closedToggle) {
        closedToggle.onchange = (e) => {
            state.showClosed = e.target.checked; // Write to global state
            // saveViewSettings(); // <-- REMOVE this line
            void rerenderCurrentBoard();
        };
    }
    if (wipToggle) {
        wipToggle.onchange = (e) => {
            state.showWIP = e.target.checked;
            void rerenderCurrentBoard();
        };
    }

    const taskHorizonValue = footerContainer.querySelector('#task-horizon-inline-value');
    if (taskHorizonValue) {
        const updateTaskHorizonEditorState = (editorInput) => {
            if (!editorInput) {return;}
            const minimum = getTaskHorizonMinimumDays();
            const parsedValue = parseTaskHorizonDays(editorInput.value.trim());
            const isBelowMinimum = minimum !== null &&
                parsedValue !== null &&
                parsedValue < minimum;

            editorInput.classList.toggle('border-red-400', isBelowMinimum);
            editorInput.classList.toggle('text-red-600', isBelowMinimum);
            editorInput.classList.toggle('border-gray-300', !isBelowMinimum);
            editorInput.classList.toggle('text-gray-700', !isBelowMinimum);
            editorInput.classList.toggle('dark:text-gray-200', !isBelowMinimum);
        };

        const enterTaskHorizonEditMode = (initialValue) => {
            taskHorizonValue.outerHTML = `
                <span id="task-horizon-inline-editing" class="inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-200">
                    <span>within</span>
                    <input
                        id="task-horizon-inline-editor"
                        type="number"
                        min="0"
                        step="1"
                        value="${initialValue}"
                        class="w-16 rounded-sm border-b border-gray-300 bg-transparent px-1 py-0.5 text-center text-sm font-medium text-gray-700 outline-none transition-colors hover:border-[var(--dfc-brand-primary)] focus:shadow-[inset_0_-2px_0_rgba(71,85,105,0.55)] dark:text-gray-200"
                    >
                    <span>days</span>
                </span>
            `;

            const editorInput = footerContainer.querySelector('#task-horizon-inline-editor');
            if (!editorInput) {return null;}

            updateTaskHorizonEditorState(editorInput);
            editorInput.focus();
            editorInput.select();
            editorInput.addEventListener('input', () => updateTaskHorizonEditorState(editorInput));
            return editorInput;
        };

        const saveTaskHorizonPreference = async () => {
            const editorInput = footerContainer.querySelector('#task-horizon-inline-editor');
            const rawValue = editorInput ? editorInput.value.trim() : '';
            const minimum = getTaskHorizonMinimumDays();

            if (rawValue === '') {
                if (state.currentUserProfile) {state.currentUserProfile.taskVisibilityHorizonDays = null;}
                await updateUserDocument({ taskVisibilityHorizonDays: null });
                void rerenderCurrentBoard();
                return;
            }

            let normalizedValue = parseTaskHorizonDays(rawValue);
            if (normalizedValue === null) {
                const fallbackValue = state.currentUserProfile?.taskVisibilityHorizonDays;
                if (fallbackValue === null || fallbackValue === undefined || fallbackValue === '') {
                    void rerenderCurrentBoard();
                    return;
                }
                void rerenderCurrentBoard();
                return;
            }

            if (minimum !== null && normalizedValue < minimum) {
                normalizedValue = minimum;
            }

            if (state.currentUserProfile) {state.currentUserProfile.taskVisibilityHorizonDays = normalizedValue;}
            await updateUserDocument({ taskVisibilityHorizonDays: normalizedValue });
            void rerenderCurrentBoard();
        };

        taskHorizonValue.addEventListener('click', async () => {
            const currentValue = state.currentUserProfile?.taskVisibilityHorizonDays;
            const minimum = getTaskHorizonMinimumDays();

            const currentEffectiveValue = currentValue === null || currentValue === undefined || currentValue === ''
                ? ((minimum ?? Number(taskHorizonValue.dataset.defaultValue)) || 30)
                : (getCurrentUserTaskHorizonDays() ?? currentValue);
            const editorInput = enterTaskHorizonEditMode(currentEffectiveValue);
            if (!editorInput) {return;}

            editorInput.addEventListener('blur', saveTaskHorizonPreference, { once: true });
            editorInput.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    editorInput.blur();
                }
                if (e.key === 'Escape') {
                    e.preventDefault();
                    void rerenderCurrentBoard();
                }
            });
        });
    }
}
